import { useEffect, useMemo, useState } from 'react';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import pageMarkup from './pageContent';
import HomeMediaSections from './HomeMediaSections';
import AccountPage from './AccountPage';
import { bootstrapMegaMenu, bootstrapPages, bootstrapProducts, createOrder, fetchMegaMenu, fetchOrder, fetchPages, fetchProducts } from './contentApi';
import './App.css';

// Number of video bands the original static markup carried (now admin-managed).
const STATIC_VIDEO_COUNT = 4;

const COLLECTION_PAGES = {
  'outdoor-collection': {
    title: 'Outdoor Collection',
    tagline: 'Living rooms without walls',
    intro: 'Weather-resistant sofas, dining sets and accessories designed to turn terraces, gardens and poolsides into true outdoor living rooms.',
    hero: '/Outdoor-2026_HP2_Brochure_EN_Desktop.jpg',
    heroMobile: '/Outdoor-2026_HP2_Brochure_EN_Mobile.jpg',
    items: [
      { name: 'Formentera Soleil', href: '/products/outdoor-sofas', image: '/Formnetera-Soleil_Inspi_0.jpg', text: 'Modular outdoor seating in solution-dyed fabric with a teak base.' },
      { name: 'Bubble Outdoor', href: '/products/outdoor-sofas', image: '/Bubble-Lit_Produit_16-9_opt.jpg', text: 'Rounded volumes and soft cushions, re-imagined for open air.' },
      { name: 'Mah Jong Outdoor', href: '/products/outdoor-furnitures', image: '/Mah-Jong-KT-Rouge_Produit_16-9.jpg', text: 'The iconic modular composition, in fade-resistant outdoor covers.' },
    ],
  },
  'spring-summer': {
    title: 'Spring-Summer',
    tagline: 'The 2026 seasonal collection',
    intro: 'Lighter volumes, sun-washed colours and natural materials — the pieces our designers created for the warmer months.',
    hero: '/home_CPE_2026_desktop_Formentera-Soleil_VDEF.jpg',
    heroMobile: '/home_CPE_2025_mobile_Aqua_V2.jpg',
    items: [
      { name: 'Aqua', href: '/products/sofas', image: '/home_CPE_2025_desktop_Aqua_V4.jpg', text: 'Fluid lines and water-inspired tones for a serene living space.' },
      { name: 'Script', href: '/products/sofas', image: '/Script_Produit_Desk.jpg', text: 'A graphic silhouette softened by generous, welcoming cushions.' },
      { name: 'Bubble Curve', href: '/products/sofas', image: '/Bubble-Lit_Produit_1-1_opt.jpg', text: 'Curved seating that invites conversation and long summer evenings.' },
    ],
  },
  'the-deco-boutique': {
    title: 'The Deco Boutique',
    tagline: 'Finishing touches',
    intro: 'Rugs, lighting, mirrors and decorative objects selected to complete an interior — the details that make a room feel finished.',
    hero: '/Slide_HP2_Catal_2026_Desk_EN.jpg',
    heroMobile: '/CH_Watches-and-Wonders_2026_mob_01.jpg',
    items: [
      { name: 'Lighting', href: '/products/lamps', image: '/Deltalis_Desktop.jpg', text: 'Sculptural floor and table lamps that shape the evening light.' },
      { name: 'Rugs & Textiles', href: '/products/rugs', image: '/Story_Mah-Jong-Rouge_03.jpg', text: 'Hand-finished rugs, throws and cushions in seasonal palettes.' },
      { name: 'Objects', href: '/products/home-furnishings', image: '/FR_Hotel_Peninsula_2026_mob_02.jpg', text: 'Vases, trays and mirrors chosen piece by piece by our stylists.' },
    ],
  },
};

const PRODUCT_CATEGORY_LABELS = {
  'outdoor-collection': 'Outdoor Collection', 'spring-summer': 'Spring-Summer',
  'the-deco-boutique': 'The Deco Boutique',
  sofas: 'Sofas & Sofa beds', armchairs: 'Armchairs', 'cocktails-tables': 'Cocktail Tables',
  'tv-units': 'TV Units', 'bookcases-and-cabinets': 'Bookcases and Cabinets',
  'consoles-occasional-furniture': 'Consoles, Occasional Furniture', tables: 'Dining tables',
  'chairs-stools-benches': 'Chairs, Stools & Benches', 'sideboards-columns-china-units': 'Dining Room Storage',
  desks: 'Desks', 'office-chairs': 'Office Chairs', beds: 'Beds', wardrobes: 'Wardrobes',
  'other-bedroom-furniture': 'Other Bedroom Furniture', 'outdoor-sofas': 'Outdoor Sofas & Armchairs',
  'outdoor-furnitures': 'Outdoor Furniture', 'home-furnishings': 'Home Furnishings', lamps: 'Lighting',
  rugs: 'Rugs', cushions: 'Cushions', mirrors: 'Mirrors', 'outdoor-decoration': 'Outdoor Decoration',
};

const EMPTY_MENU = { columns: [], promos: [] };

/**
 * Teach the router about the catalogue slugs the admin menu points at.
 *
 * A column added in the studio can link to any listing, so its slugs have to be
 * routable even though the front end has never heard of them.
 */
function adoptMenuCategories(menu) {
  (menu?.columns || []).forEach((column) => {
    column.links.forEach((link) => {
      if (link.categorySlug && !PRODUCT_CATEGORY_LABELS[link.categorySlug]) {
        PRODUCT_CATEGORY_LABELS[link.categorySlug] = link.label;
      }
    });
  });
  return menu;
}

adoptMenuCategories(bootstrapMegaMenu);

/** Where a menu link sends the visitor — catalogue links stay inside the SPA. */
function menuHref(link) {
  if (link.categorySlug && PRODUCT_CATEGORY_LABELS[link.categorySlug]) return `/products/${link.categorySlug}`;
  return link.href || '#';
}

const ACCOUNT_ROUTES = ['login', 'register', 'account'];
const ACCOUNT_LINK_PATTERN = /\/(login|register|selections|wishlist|customer\/account)/i;
const HIDDEN_PRIMARY_MENU_IDS = new Set([
  'menu-collaborations',
  'see-all-stores',
  'catalogs',
  'mag',
  'roche-bobois',
]);

const CONTENT_PAGE_LINKS = {
  designers: 'designers', contact: 'contact', careers: 'careers',
  'trade & contract': 'trade-and-contract', 'becoming a franchisee': 'becoming-a-franchisee',
  investors: 'investors', 'maintenance guide': 'maintenance-guide',
  'commercial warranty contract': 'commercial-warranty',
  'privacy and data protection policy': 'privacy-policy', 'legal notices': 'legal-notices',
  'cookies settings': 'cookie-settings', 'cookie settings': 'cookie-settings',
  'access dedicated space': 'access-dedicated-space',
  'decoration consulting service': 'decoration-consulting', 'quick ship service': 'quick-ship',
  catalogues: 'catalogues', delivery: 'delivery', values: 'values', sustainability: 'sustainability',
  'eco-design': 'eco-design', 'virtual museum': 'virtual-museum', 'brand history': 'brand-history',
  'terms of use': 'terms-of-use', showrooms: 'showrooms',
};

function containsHiddenPrimaryMenuItem(node) {
  if (HIDDEN_PRIMARY_MENU_IDS.has(node?.attribs?.id)) return true;
  return Array.isArray(node?.children) && node.children.some(containsHiddenPrimaryMenuItem);
}

function getRoute() {
  const pathname = window.location.pathname.replace(/^\/+(?:en\/)?/, '').replace(/\/$/, '');
  const legacyHash = (window.location.hash || '').replace(/^#\/?/, '').split('?')[0].replace(/\/$/, '');
  const path = pathname || legacyHash;
  if (path === 'admin') return 'admin';
  if (path === 'login' || path === 'register' || path === 'account') return path;
  if (path.startsWith('pages/')) return path;
  if (Object.values(CONTENT_PAGE_LINKS).includes(path)) return `pages/${path}`;
  if (path === 'products' || path === 'products-1') return 'products/all';
  if (path.startsWith('product/')) return path;
  if (path.startsWith('checkout/')) return path;
  if (path.startsWith('thank-you/')) return path;
  if (path.startsWith('products/') && PRODUCT_CATEGORY_LABELS[path.slice(9)]) return path;
  return COLLECTION_PAGES[path] ? path : 'home';
}

function navigateTo(path) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function ContentPage({ slug, pages, loading }) {
  const page = pages.find((item) => item.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = `${page?.title || 'Page'} | MONALISA LIVING`;
  }, [page]);

  if (loading) {
    return <main id="maincontent" className="cms-page cms-page-empty" aria-busy="true">
      <p className="cms-page-eyebrow">MONALISA LIVING</p><p>Loading page…</p>
    </main>;
  }

  if (!page) {
    return <main id="maincontent" className="cms-page cms-page-empty">
      <p className="cms-page-eyebrow">MONALISA LIVING</p><h1>Page not found</h1>
      <p>This page is unavailable or has not been published yet.</p><a href="/">Return home</a>
    </main>;
  }

  const paragraphs = page.body.split(/\n\s*\n/).filter(Boolean);
  return <main id="maincontent" className="cms-page">
    {page.heroImage && <div className="cms-page-hero"><img src={page.heroImage} alt="" /></div>}
    <article className="cms-page-inner">
      <p className="cms-page-eyebrow">{page.eyebrow}</p>
      <h1>{page.title}</h1>
      {page.intro && <p className="cms-page-intro">{page.intro}</p>}
      <div className="cms-page-body">{paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
      {page.ctaLabel && <a className="cms-page-cta" href={page.ctaHref || '/'}>{page.ctaLabel}</a>}
    </article>
  </main>;
}

function ProductCategoryPage({ slug }) {
  const isAllProducts = slug === 'all';
  const title = isAllProducts ? 'Products' : (PRODUCT_CATEGORY_LABELS[slug] || 'Products');
  const [sort, setSort] = useState('relevance');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [products, setProducts] = useState(bootstrapProducts || []);
  const searchTerm = new URLSearchParams(window.location.search).get('search')?.trim().toLowerCase() || '';
  const categoryProducts = isAllProducts ? products : products.filter((product) => product.categorySlug === slug);
  const searchedProducts = searchTerm ? categoryProducts.filter((product) =>
    [product.name, product.type, product.description, PRODUCT_CATEGORY_LABELS[product.categorySlug]]
      .some((value) => String(value || '').toLowerCase().includes(searchTerm))) : categoryProducts;
  const availableCategories = [...new Set(products.map((product) => product.categorySlug))]
    .filter((category) => PRODUCT_CATEGORY_LABELS[category]);
  const visibleProducts = [...searchedProducts].sort((a, b) => {
    if (sort === 'name') return a.name.localeCompare(b.name);
    if (sort === 'price-low') return Number(a.price || 0) - Number(b.price || 0);
    if (sort === 'price-high') return Number(b.price || 0) - Number(a.price || 0);
    return 0;
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = `${title} | MONALISA LIVING`;
  }, [title]);

  useEffect(() => {
    if (bootstrapProducts) return undefined;
    let active = true;
    fetchProducts().then((items) => { if (active) setProducts(items); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const toggleSaved = (name) => setSaved((current) => current.includes(name)
    ? current.filter((item) => item !== name) : [...current, name]);

  return (
    <main id="maincontent" className="product-listing-page">
      <header className="product-listing-header">
        <h1>{searchTerm ? `Search results for “${new URLSearchParams(window.location.search).get('search')}”` : title}</h1>
        <p>{isAllProducts
          ? 'Explore the complete MONALISA LIVING collection—furniture, lighting and accessories created for considered contemporary interiors.'
          : `A MONALISA LIVING ${title.toLowerCase()} collection where refined design, practical comfort and considered details bring rhythm and character to the room.`}</p>
      </header>
      {isAllProducts && <nav className="shop-category-nav" aria-label="Shop by category">
        <a className="is-active" href="/products">All products</a>
        {availableCategories.map((category) => (
          <a key={category} href={`/products/${category}`}>{PRODUCT_CATEGORY_LABELS[category]}</a>
        ))}
      </nav>}
      <section className="product-listing-controls" aria-label="Product controls">
        <div>
          <button type="button" className="product-filter-button" aria-expanded={filtersOpen}
            onClick={() => setFiltersOpen((open) => !open)}>Filter <span aria-hidden="true">☷</span></button>
          <label className="product-sort">
            <span className="sr-only">Sort products</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              <option value="relevance">Relevance</option><option value="name">Name</option>
              <option value="price-low">Price: Low to high</option><option value="price-high">Price: High to low</option>
            </select>
          </label>
        </div>
        <span>{visibleProducts.length} Products</span>
      </section>
      {filtersOpen && <div className="product-filter-panel">
        <span>Availability</span><button type="button">In stock</button>
        <span>Collection</span><button type="button">New collection</button>
      </div>}
      <section className="product-listing-grid" aria-label={`${title} products`}>
        {visibleProducts.map((product) => {
          return <ListingProductCard key={product.id} product={product} title={title}
            saved={saved.includes(product.id)} onToggleSaved={() => toggleSaved(product.id)} />;
        })}
        {!visibleProducts.length && <div className="product-listing-empty">
          <h2>No products in this category yet</h2><p>New products will appear here when they are published.</p>
        </div>}
      </section>
    </main>
  );
}

function ListingProductCard({ product, title, saved, onToggleSaved }) {
  const images = product.images?.length ? product.images : [];
  const [activeImage, setActiveImage] = useState(0);
  const price = product.price === null || product.price === undefined ? '' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: product.currency || 'USD', maximumFractionDigits: 2,
  }).format(product.price);
  const detailSlug = String(product.id || product.name).replace(/^product-/, '');

  return <article className="listing-product-card" role="link" tabIndex="0"
    aria-label={`View ${product.name}`}
    onClick={(event) => { if (!event.target.closest('button')) navigateTo(`/product/${detailSlug}`); }}
    onKeyDown={(event) => { if (event.key === 'Enter') navigateTo(`/product/${detailSlug}`); }}>
    <div className={`listing-product-media${activeImage > 0 ? ' is-final-look' : ''}${product.badge ? ' has-badge' : ''}`}
      onMouseEnter={() => { if (images.length > 1) setActiveImage(1); }}
      onMouseLeave={() => setActiveImage(0)}>
      {product.badge && <span className="listing-product-badge">{product.badge}</span>}
      <button type="button" className={saved ? 'is-saved' : ''}
        aria-label={`${saved ? 'Remove' : 'Save'} ${product.name}`} aria-pressed={saved}
        onClick={onToggleSaved}>{saved ? '♥' : '♡'}</button>
      {images[activeImage] && <img src={images[activeImage]} alt={`${product.name} ${title}`} loading="lazy" />}
      {images.length > 1 && <span className="listing-product-view-label">{activeImage === 0 ? 'View final look' : 'Product view'}</span>}
      {images.length > 1 && <div className="listing-product-dots" aria-label={`${product.name} images`}>
        {images.map((image, index) => <button key={image} type="button"
          aria-label={`View image ${index + 1}`} aria-current={index === activeImage ? 'true' : undefined}
          onClick={() => setActiveImage(index)} />)}
      </div>}
    </div>
    <div className="listing-product-copy"><p>{product.type || title}</p><h2>{product.name}</h2>
      {price && <span>{price}</span>}</div>
  </article>;
}

function ProductDetailPage({ slug, products }) {
  const product = products.find((item) => String(item.id) === slug || String(item.id) === `product-${slug}`);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = `${product?.name || 'Product'} | MONALISA LIVING`;
  }, [product]);

  if (!product) return <main id="maincontent" className="product-detail-empty">
    <h1>Product not found</h1><a href="/products">Return to products</a>
  </main>;

  const price = product.price === null || product.price === undefined ? '' : new Intl.NumberFormat('en-US', {
    style: 'currency', currency: product.currency || 'USD', maximumFractionDigits: 2,
  }).format(product.price);
  const category = PRODUCT_CATEGORY_LABELS[product.categorySlug] || 'Products';

  return <main id="maincontent" className="product-detail-page">
    <section className="product-detail-gallery" aria-label={`${product.name} gallery`}>
      {(product.images || []).map((src, index) => <figure className={index === 0 ? 'is-product-shot' : 'is-lifestyle-shot'} key={`${src}-${index}`}>
        <img src={src} alt={`${product.name} view ${index + 1}`} />
        <figcaption>{index === 0 ? 'Product view' : `Inspiration ${index}`}</figcaption>
      </figure>)}
    </section>
    <aside className="product-detail-info">
      <nav className="product-detail-breadcrumb"><a href="/">Home</a><span>/</span>
        <a href={`/products/${product.categorySlug}`}>{category}</a><span>/</span><span>{product.name}</span></nav>
      <p className="product-detail-type">{product.type || category}</p>
      <h1>{product.name}</h1>
      <div className="product-detail-tags"><span>Made to order</span><span>Ref. {String(product.id).replace(/^product-/, '').toUpperCase()}</span></div>
      {product.description && <p className="product-detail-description">{product.description}</p>}
      <div className="product-detail-meta"><span>Category</span><strong>{category}</strong></div>
      {price && <p className="product-detail-price">{price}</p>}
      <button type="button" className="product-detail-action" onClick={() => navigateTo(`/checkout/${slug}`)}>Order now</button>
      <a className="product-detail-consult" href="/contact">Contact a design consultant</a>
      <div className="product-detail-services">
        <div><strong>Personalisation</strong><span>Materials, colours and finishes selected for your interior.</span></div>
        <div><strong>Delivery & installation</strong><span>Specialist handling and placement by our delivery team.</span></div>
        <div><strong>Product care</strong><span>Guidance and after-sales support from our consultants.</span></div>
      </div>
      <p className="product-detail-note">Availability and lead times may vary. Contact us for dimensions, finishes and delivery information.</p>
    </aside>
  </main>;
}

function CheckoutPage({ slug, products }) {
  const product = products.find((item) => String(item.id) === slug || String(item.id) === `product-${slug}`);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState({ customerName: '', email: '', phone: '', address: '', city: '', postalCode: '', notes: '', paymentMethod: 'cash_on_delivery' });
  const change = (event) => setValues((current) => ({ ...current, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault(); setSubmitting(true); setError('');
    try {
      const order = await createOrder({ ...values, productId: product.id, quantity });
      window.location.assign(`/thank-you/${order.number}?token=${encodeURIComponent(order.token)}`);
    } catch (exception) { setError(exception.message); setSubmitting(false); }
  };

  if (!product) return <main id="maincontent" className="checkout-empty"><h1>Product unavailable</h1><a href="/products">Return to products</a></main>;
  const total = Number(product.price || 0) * quantity;
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency || 'BDT', maximumFractionDigits: 2 });

  return <main id="maincontent" className="checkout-page">
    <header className="checkout-heading"><p>Secure checkout</p><h1>Complete your order</h1><span>All fields marked * are required.</span></header>
    <form className="checkout-layout" onSubmit={submit}>
      <section className="checkout-form-card">
        <div className="checkout-step"><span>01</span><div><h2>Contact information</h2><p>We will send your confirmation and delivery updates here.</p></div></div>
        <div className="checkout-fields">
          <label className="is-wide"><span>Full name *</span><input name="customerName" value={values.customerName} onChange={change} required /></label>
          <label><span>Email *</span><input type="email" name="email" value={values.email} onChange={change} required /></label>
          <label><span>Phone *</span><input name="phone" value={values.phone} onChange={change} required /></label>
        </div>
        <div className="checkout-step"><span>02</span><div><h2>Delivery address</h2><p>Our team will confirm delivery timing after your order.</p></div></div>
        <div className="checkout-fields">
          <label className="is-wide"><span>Street address *</span><input name="address" value={values.address} onChange={change} required /></label>
          <label><span>City *</span><input name="city" value={values.city} onChange={change} required /></label>
          <label><span>Postal code</span><input name="postalCode" value={values.postalCode} onChange={change} /></label>
          <label className="is-wide"><span>Order notes</span><textarea name="notes" value={values.notes} onChange={change} rows="3" /></label>
        </div>
        <div className="checkout-step"><span>03</span><div><h2>Payment method</h2><p>No card details are collected on this website.</p></div></div>
        <div className="checkout-payment">
          <label><input type="radio" name="paymentMethod" value="cash_on_delivery" checked={values.paymentMethod === 'cash_on_delivery'} onChange={change} /><span><strong>Cash on delivery</strong><small>Pay when your order is delivered.</small></span></label>
          <label><input type="radio" name="paymentMethod" value="bank_transfer" checked={values.paymentMethod === 'bank_transfer'} onChange={change} /><span><strong>Bank transfer</strong><small>Transfer instructions will be confirmed by our team.</small></span></label>
        </div>
        {error && <p className="checkout-error" role="alert">{error}</p>}
      </section>
      <aside className="checkout-summary">
        <p className="checkout-summary-label">Order summary</p>
        <div className="checkout-product"><img src={product.images?.[0]} alt="" /><div><small>{product.type}</small><strong>{product.name}</strong><span>{money.format(Number(product.price || 0))}</span></div></div>
        <label className="checkout-quantity"><span>Quantity</span><select value={quantity} onChange={(event) => setQuantity(Number(event.target.value))}>{[1,2,3,4,5].map((value) => <option key={value}>{value}</option>)}</select></label>
        <dl><div><dt>Subtotal</dt><dd>{money.format(total)}</dd></div><div><dt>Delivery</dt><dd>Complimentary</dd></div><div className="is-total"><dt>Total</dt><dd>{money.format(total)}</dd></div></dl>
        <button type="submit" disabled={submitting || product.price == null}>{submitting ? 'Placing order…' : 'Place order'}</button>
        {product.price == null && <p className="checkout-error">This product needs a price before online ordering.</p>}
        <p className="checkout-secure">Your details are securely submitted to MONALISA LIVING.</p>
      </aside>
    </form>
  </main>;
}

function ThankYouPage({ number }) {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { fetchOrder(number, token).then(setOrder).catch(() => setFailed(true)); }, [number, token]);
  if (failed) return <main id="maincontent" className="thank-page"><h1>Order not found</h1><a href="/products">Continue shopping</a></main>;
  if (!order) return <main id="maincontent" className="thank-page" aria-busy="true"><p>Loading your order…</p></main>;
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: order.currency, maximumFractionDigits: 2 });
  const invoice = `/orders/${encodeURIComponent(order.number)}/invoice?token=${encodeURIComponent(token)}`;
  return <main id="maincontent" className="thank-page">
    <div className="thank-mark">✓</div><p className="thank-eyebrow">Order confirmed</p><h1>Thank you, {order.customerName}.</h1>
    <p className="thank-intro">Your order has been received. A confirmation will be sent to <strong>{order.email}</strong>.</p>
    <section className="thank-order"><header><span>Order number</span><strong>{order.number}</strong></header>
      <div><img src={order.productImage} alt="" /><p><small>{order.productType}</small><strong>{order.productName}</strong><span>Quantity: {order.quantity}</span></p><b>{money.format(order.total)}</b></div>
    </section>
    <div className="thank-actions"><a className="is-primary" href={invoice}>Download invoice</a><a href="/products">Continue shopping</a></div>
    <p className="thank-help">Questions about your order? Contact our team and quote <strong>{order.number}</strong>.</p>
  </main>;
}

function DecoProductCard({ id, category, name, images, note, badge }) {
  const [activeImage, setActiveImage] = useState(0);
  const productImages = images?.filter(Boolean) || [];
  const moveImage = (direction) => {
    setActiveImage((current) => (current + direction + productImages.length) % productImages.length);
  };

  return (
    <article className="deco-product-card" role="link" tabIndex="0"
      onClick={(event) => { if (id && !event.target.closest('button')) navigateTo(`/product/${String(id).replace(/^product-/, '')}`); }}
      onKeyDown={(event) => { if (id && event.key === 'Enter') navigateTo(`/product/${String(id).replace(/^product-/, '')}`); }}>
      <div className={`deco-product-media${activeImage > 0 ? ' is-lifestyle' : ' is-product-view'}`}
        onMouseEnter={() => { if (productImages.length > 1) setActiveImage(1); }}
        onMouseLeave={() => setActiveImage(0)}>
        {badge && <span className="deco-product-badge">{badge}</span>}
        <button className="deco-product-favourite" type="button" aria-label={`Save ${name}`}>♡</button>
        {productImages[activeImage] && <img src={productImages[activeImage]} alt={`${name} by MONALISA LIVING — view ${activeImage + 1}`} loading="lazy" />}
        {productImages.length > 1 && (
          <>
            <button className="deco-product-arrow deco-product-prev" type="button"
              aria-label={`Previous ${name} image`} onClick={() => moveImage(-1)}>‹</button>
            <button className="deco-product-arrow deco-product-next" type="button"
              aria-label={`Next ${name} image`} onClick={() => moveImage(1)}>›</button>
            <div className="deco-product-dots" aria-label={`${name} image selection`}>
              {productImages.map((image, index) => (
                <button key={image} type="button" aria-label={`View ${name} image ${index + 1}`}
                  aria-current={index === activeImage ? 'true' : undefined}
                  onClick={() => setActiveImage(index)} />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="deco-product-info">
        <div>
          <p>{category}</p>
          <h2>{name}</h2>
        </div>
        {note && <span>{note}</span>}
      </div>
    </article>
  );
}

function DecoBoutiquePage({ adminProducts }) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = 'The Deco Boutique | MONALISA LIVING';
  }, []);

  const products = adminProducts
    .filter((product) => product.collections?.includes('the-deco-boutique'))
    .map((product) => ({ id: product.id, category: product.type || PRODUCT_CATEGORY_LABELS[product.categorySlug],
      name: product.name, images: product.images, badge: product.badge,
      note: product.price == null ? '' : `${product.currency} ${Number(product.price).toLocaleString()}` }));

  return (
    <main id="maincontent" className="deco-boutique-page">
      <section className="deco-boutique-intro">
        <p className="deco-boutique-new">New</p>
        <h1>Deco Boutique</h1>
        <p className="deco-boutique-lead">
          <strong>MONALISA LIVING Deco Boutique is a refined collection of rugs, decorative objects and accessories—created to bring character and warmth to every interior.</strong>
        </p>
        <p className="deco-boutique-copy">
          A curated selection for design lovers, chosen with care to make every room feel personal, balanced and beautifully finished.
        </p>
      </section>

      <section className="deco-products" aria-labelledby="deco-products-title">
        <div className="deco-products-heading">
          <h2 id="deco-products-title">MONALISA LIVING Selection</h2>
          <span>{products.length} Products</span>
        </div>
        <div className="deco-products-grid">
          {products.map((product) => <DecoProductCard key={product.name} {...product} />)}
          {!products.length && <p className="collection-products-empty">No published products in this collection yet.</p>}
        </div>
      </section>
    </main>
  );
}

function SpringSummerPage({ adminProducts }) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = 'Spring-Summer 2026 | MONALISA LIVING';
  }, []);

  const products = adminProducts
    .filter((product) => product.collections?.includes('spring-summer'))
    .map((product) => ({ id: product.id, category: product.type || PRODUCT_CATEGORY_LABELS[product.categorySlug],
      name: product.name, images: product.images, badge: product.badge,
      note: product.price == null ? '' : `${product.currency} ${Number(product.price).toLocaleString()}` }));

  return (
    <main id="maincontent" className="spring-summer-page">
      <section className="spring-summer-intro">
        <h1>Creations</h1>
        <p className="spring-summer-season">Spring–Summer 2026</p>
        <p className="spring-summer-lead">
          Know-how, creativity and innovation are at the heart of MONALISA LIVING.
          <br />For Spring–Summer 2026, we introduce fresh shapes, calm colours and refined material combinations.
        </p>
        <div className="spring-summer-actions">
          <a href="#footercontent"><span aria-hidden="true">→</span> Find your showroom</a>
          <a href="#spring-products"><span aria-hidden="true">→</span> Discover the collection</a>
        </div>
      </section>

      <section className="spring-products" id="spring-products" aria-labelledby="spring-products-title">
        <div className="spring-products-heading">
          <h2 id="spring-products-title">MONALISA LIVING New Collection</h2>
          <span>{products.length} Products</span>
        </div>
        <div className="deco-products-grid spring-products-grid">
          {products.map((product) => <DecoProductCard key={product.name} {...product} />)}
          {!products.length && <p className="collection-products-empty">No published products in this collection yet.</p>}
        </div>
      </section>
    </main>
  );
}

function OutdoorCollectionPage({ adminProducts }) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = 'Outdoor Collection 2026 | MONALISA LIVING';
  }, []);

  const products = adminProducts
    .filter((product) => product.collections?.includes('outdoor-collection'))
    .map((product) => ({ id: product.id, category: product.type || PRODUCT_CATEGORY_LABELS[product.categorySlug],
      name: product.name, images: product.images, badge: product.badge,
      note: product.price == null ? '' : `${product.currency} ${Number(product.price).toLocaleString()}` }));

  return (
    <main id="maincontent" className="outdoor-collection-page">
      <section className="outdoor-collection-intro">
        <h1>Outdoor</h1>
        <p className="outdoor-collection-year">Collection 2026</p>
        <p className="outdoor-collection-tagline">Take indoor comfort outdoors</p>
        <p className="outdoor-collection-lead">
          Designed for terraces, balconies and gardens, the new MONALISA LIVING collection brings refined comfort outdoors—extending every moment from the first sunny days.
        </p>
        <div className="outdoor-collection-actions">
          <a href="#outdoor-products"><span aria-hidden="true">→</span> Discover the collection</a>
          <a href="#footercontent"><span aria-hidden="true">→</span> Find your showroom</a>
        </div>
      </section>

      <section className="outdoor-products" id="outdoor-products" aria-labelledby="outdoor-products-title">
        <div className="outdoor-products-heading">
          <h2 id="outdoor-products-title">MONALISA LIVING Outdoor Selection</h2>
          <span>{products.length} Products</span>
        </div>
        <div className="deco-products-grid outdoor-products-grid">
          {products.map((product) => <DecoProductCard key={product.name} {...product} />)}
          {!products.length && <p className="collection-products-empty">No published products in this collection yet.</p>}
        </div>
      </section>
    </main>
  );
}

function CollectionPage({ slug }) {
  const page = COLLECTION_PAGES[slug];

  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.title = `${page.title} | MONALISA LIVING`;
  }, [page.title]);

  return (
    <main id="maincontent" className="collection-page">
      <section className="collection-hero">
        <picture>
          <source media="(max-width: 833.98px)" srcSet={page.heroMobile} />
          <img src={page.hero} alt={page.title} />
        </picture>
        <div className="collection-hero-text">
          <p className="collection-eyebrow">{page.tagline}</p>
          <h1>{page.title}</h1>
        </div>
      </section>

      <section className="collection-intro">
        <p>{page.intro}</p>
      </section>

      <section className="collection-grid">
        {page.items.map((item) => (
          <article key={item.name} className="collection-card">
            {item.href
              ? <a href={item.href}><img src={item.image} alt={item.name} loading="lazy" /></a>
              : <img src={item.image} alt={item.name} loading="lazy" />}
            <h2>{item.href ? <a href={item.href}>{item.name}</a> : item.name}</h2>
            <p>{item.text}</p>
          </article>
        ))}
      </section>
    </main>
  );
}

function FooterSocialIcon({ type }) {
  const paths = {
    instagram: <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.42-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.17-.42-.37-1.06-.42-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.42 2.21 8.8 2.2 12 2.2Zm0 1.8c-3.14 0-3.5.01-4.74.07-.9.04-1.38.19-1.7.31-.43.17-.73.37-1.05.69-.32.32-.52.62-.69 1.05-.12.32-.27.8-.31 1.7C3.45 8.5 3.44 8.86 3.44 12s.01 3.5.07 4.74c.04.9.19 1.38.31 1.7.17.43.37.73.69 1.05.32.32.62.52 1.05.69.32.12.8.27 1.7.31 1.24.06 1.6.07 4.74.07s3.5-.01 4.74-.07c.9-.04 1.38-.19 1.7-.31.43-.17.73-.37 1.05-.69.32-.32.52-.62.69-1.05.12-.32.27-.8.31-1.7.06-1.24.07-1.6.07-4.74s-.01-3.5-.07-4.74c-.04-.9-.19-1.38-.31-1.7a2.82 2.82 0 0 0-.69-1.05 2.82 2.82 0 0 0-1.05-.69c-.32-.12-.8-.27-1.7-.31C15.5 4.01 15.14 4 12 4Zm0 3.03a4.97 4.97 0 1 1 0 9.94 4.97 4.97 0 0 1 0-9.94Zm0 1.8a3.17 3.17 0 1 0 0 6.34 3.17 3.17 0 0 0 0-6.34Zm5.2-2.98a1.16 1.16 0 1 1 0 2.32 1.16 1.16 0 0 1 0-2.32Z" />,
    facebook: <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5h1.65V3.63A22.2 22.2 0 0 0 14.28 3.5c-2.4 0-4.03 1.46-4.03 4.15V9.9H7.6V13h2.65v8h3.25Z" />,
    pinterest: <path d="M12 2.5a9.5 9.5 0 0 0-3.46 18.34c-.08-.77-.15-1.96.03-2.8.17-.77 1.1-4.66 1.1-4.66s-.28-.56-.28-1.4c0-1.3.76-2.28 1.7-2.28.8 0 1.19.6 1.19 1.33 0 .8-.52 2-.78 3.12-.23.93.47 1.7 1.38 1.7 1.66 0 2.94-1.75 2.94-4.28 0-2.24-1.6-3.8-3.9-3.8-2.66 0-4.22 2-4.22 4.06 0 .8.31 1.66.7 2.13.08.1.09.18.07.28l-.26 1.06c-.04.17-.14.2-.32.12-1.2-.56-1.95-2.3-1.95-3.7 0-3.02 2.19-5.79 6.32-5.79 3.32 0 5.9 2.36 5.9 5.52 0 3.3-2.08 5.95-4.96 5.95-.97 0-1.88-.5-2.19-1.1l-.6 2.28c-.21.83-.79 1.87-1.18 2.5A9.5 9.5 0 1 0 12 2.5Z" />,
    youtube: <path d="M21.6 8.2s-.19-1.36-.78-1.96c-.75-.78-1.58-.79-1.96-.83C16.13 5.2 12 5.2 12 5.2h-.01s-4.12 0-6.85.21c-.38.05-1.21.05-1.96.83-.6.6-.78 1.96-.78 1.96S2.2 9.8 2.2 11.4v1.5c0 1.6.2 3.2.2 3.2s.19 1.36.78 1.96c.75.78 1.73.76 2.17.84 1.57.15 6.65.2 6.65.2s4.13-.01 6.86-.21c.38-.05 1.21-.06 1.96-.84.6-.6.78-1.95.78-1.95s.2-1.6.2-3.2v-1.5c0-1.6-.2-3.2-.2-3.2ZM10.05 14.7V9.5l5.3 2.61-5.3 2.59Z" />,
    linkedin: <path d="M7.1 20.5H3.9V9.9h3.2v10.6ZM5.5 8.5A1.9 1.9 0 1 1 5.5 4.7a1.9 1.9 0 0 1 0 3.8Zm14.6 12h-3.2v-5.15c0-1.23-.02-2.8-1.71-2.8-1.72 0-1.98 1.34-1.98 2.72v5.23H9.99V9.9h3.08v1.45h.04c.43-.81 1.48-1.67 3.04-1.67 3.25 0 3.85 2.14 3.85 4.93v5.89Z" />,
    tiktok: <path d="M16.6 3h-2.7v11.8a2.2 2.2 0 1 1-1.8-2.16V9.9a5 5 0 1 0 4.5 4.97V9.4a6.2 6.2 0 0 0 3.4 1.02V7.72A3.5 3.5 0 0 1 16.6 3Z" />,
    location: <path d="M12 22s7-6.1 7-13a7 7 0 1 0-14 0c0 6.9 7 13 7 13Zm0-9.5A3.5 3.5 0 1 1 12 5a3.5 3.5 0 0 1 0 7.5Z" />,
  };

  return <svg className="rb-footer-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[type]}</svg>;
}

function DigitalWebarsFooter() {
  const columns = [
    { title: 'Investors', links: [['Access dedicated space', '/access-dedicated-space']] },
    {
      title: 'Corporate',
      links: [
        ['Trade & contract', '/trade-and-contract'],
        ['Becoming a franchisee', '/becoming-a-franchisee'],
        ['Careers', '/careers'],
      ],
    },
    {
      title: 'Services',
      links: [
        ['Decoration consulting service', '/decoration-consulting'],
        ['Quick ship service', '/quick-ship'],
        ['Catalogues', '/catalogues'],
        ['Delivery', '/delivery'],
        ['Commercial warranty contract', '/commercial-warranty'],
        ['Maintenance guide', '/maintenance-guide'],
      ],
    },
    {
      title: 'Brand',
      links: [
        ['Values', '/values'],
        ['Sustainability', '/sustainability'],
        ['Eco-design', '/eco-design'],
        ['Virtual museum', '/virtual-museum'],
        ['Brand history', '/brand-history'],
      ],
    },
  ];

  const socials = [
    ['instagram', 'Instagram'],
    ['facebook', 'Facebook'],
    ['pinterest', 'Pinterest'],
    ['youtube', 'YouTube'],
    ['linkedin', 'LinkedIn'],
    ['tiktok', 'TikTok'],
  ];

  const handleSubscribe = (event) => event.preventDefault();
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer id="footercontent" className="rb-footer">
      <div className="rb-footer-inner">
        <div className="rb-footer-top">
          <div className="rb-footer-newsletter">
            <p className="rb-footer-newsletter-label">
              Receive by email the latest news and offers from MONALISA LIVING
            </p>
            <form className="rb-footer-form" onSubmit={handleSubscribe}>
              <input
                id="rb-footer-email"
                type="email"
                name="email"
                aria-label="Your email address"
                placeholder="your email address (name@domaine.com)"
                autoComplete="email"
              />
              <button type="submit">Subscribe</button>
            </form>
          </div>

          <div className="rb-footer-follow">
            <span>Follow us on:</span>
            <ul className="rb-footer-social">
              {socials.map(([type, label]) => (
                <li key={type}>
                  <a href="#/" aria-label={label}><FooterSocialIcon type={type} /></a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="rb-footer-consent">
          By entering your email in the box above, you agree to receive marketing emails from us and agree to your
          data being processed in accordance with our <a href="/terms-of-use">Terms of Use</a> and <a href="/privacy-policy">Privacy Policy</a>.
        </p>

        <div className="rb-footer-grid">
          <nav className="rb-footer-columns" aria-label="Footer navigation">
            {columns.map((column) => (
              <section key={column.title} className="rb-footer-column">
                <h2>{column.title}</h2>
                <ul>
                  {column.links.map(([label, href]) => (
                    <li key={label}><a href={href}>{label}</a></li>
                  ))}
                </ul>
              </section>
            ))}
          </nav>

          <section className="rb-footer-about">
            <h2>
              MONALISA LIVING. Celebrating French <em>art de vivre</em> around the world.
            </h2>
            <p>
              MONALISA LIVING is a world leader in furniture design and distribution. Working closely with renowned
              designers and artists, MONALISA LIVING offers a broad range of exclusive made-to-order designs,
              manufactured with a high level of customisation in small European workshops. Browse our full collection
              of premium-quality furniture, including sofas, armchairs, cocktail tables, dining chairs, sideboards,
              beds, wardrobes, storage and accessories including lighting, cushions and rugs.
            </p>
          </section>

          <div className="rb-footer-showroom">
            <a href="/showrooms" className="rb-footer-showroom-box">
              <FooterSocialIcon type="location" />
              <span className="rb-footer-showroom-label">Find a showroom</span>
              <strong>MONALISA LIVING</strong>
            </a>
          </div>
        </div>

        <div className="rb-footer-bottom">
          <span>&copy; 2026 MONALISA LIVING. All rights reserved.</span>
          <span className="rb-footer-credit">
            Designed and Developed By{' '}
            <a href="http://digitalwebars.com/" target="_blank" rel="noopener noreferrer">Digital Webars</a>
          </span>
        </div>
      </div>

      <button type="button" className="rb-footer-totop" onClick={scrollTop} aria-label="Back to top">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 6.5 5.8 12.7l1.4 1.4L12 9.3l4.8 4.8 1.4-1.4L12 6.5Zm0 5.2-6.2 6.2 1.4 1.4L12 14.5l4.8 4.8 1.4-1.4L12 11.7Z" />
        </svg>
      </button>
    </footer>
  );
}

function MenuToggle({ open, onToggle }) {
  return (
    <button className="navbar-toggler hamburger-icon react-menu-toggle" type="button" name="burgerMenu"
      aria-expanded={open} aria-controls="sg-navbar-collapse" onClick={onToggle}>
      <span className={`react-menu-symbol ${open ? 'is-close' : ''}`} aria-hidden="true">
        <i /><i /><i />
      </span>
      <span className="invis-mobile">{open ? 'Close' : 'Menu'}</span>
    </button>
  );
}

function SearchControl({ open, onToggle, products }) {
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const results = normalized ? products.filter((product) =>
    [product.name, product.type, product.description, PRODUCT_CATEGORY_LABELS[product.categorySlug]]
      .some((value) => String(value || '').toLowerCase().includes(normalized))).slice(0, 6) : [];
  const submit = (event) => {
    event.preventDefault();
    if (query.trim()) window.location.assign(`/products?search=${encodeURIComponent(query.trim())}`);
  };

  return (
    <div className="search react-header-search">
      <button className="react-search-toggle" type="button" aria-expanded={open}
        aria-controls="react-search-panel" onClick={onToggle}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.7" cy="10.7" r="6.7" />
          <path d="m16 16 5 5" />
        </svg>
        <span className="invis-mobile">Search</span>
      </button>
      {open && (
        <div className="react-search-panel" id="react-search-panel">
          <form role="search" onSubmit={submit}>
            <label htmlFor="react-search-input">Search products and collections</label>
            <div className="react-search-input-row">
              <input id="react-search-input" name="q" type="search" maxLength="80" value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="What are you looking for?" autoComplete="off" autoFocus required />
              <button className="react-search-submit" type="submit">Search</button>
              <button className="react-search-close" type="button" aria-label="Close search" onClick={onToggle}>×</button>
            </div>
            {normalized && <div className="react-search-results" aria-live="polite">
              {results.map((product) => <a key={product.id} href={`/products?search=${encodeURIComponent(product.name)}`}>
                <span className="react-search-result-image">{product.images?.[0] && <img src={product.images[0]} alt="" />}</span>
                <span><strong>{product.name}</strong><small>{product.type || PRODUCT_CATEGORY_LABELS[product.categorySlug]}</small></span>
              </a>)}
              {!results.length && <p>No matching products found.</p>}
              {results.length > 0 && <button type="submit" className="react-search-all">View all results</button>}
            </div>}
          </form>
        </div>
      )}
    </div>
  );
}

function CollectionLinks({ onNavigate }) {
  const categories = [
    ['Outdoor Collection', '/outdoor-collection'],
    ['Spring-Summer', '/spring-summer'],
    ['The Deco Boutique', '/the-deco-boutique'],
  ];

  return (
    <ul className="nav-item d-block collection-links pl-0 list-unstyled">
      {categories.map(([label, href]) => (
        <li key={label}><a href={href} onClick={onNavigate}>{label}</a></li>
      ))}
    </ul>
  );
}

/** Desktop "Products" flyout: the columns of links plus the promo cards beside them. */
function ProductsFlyout({ menu }) {
  return (
    <div className="d-flex">
      <ul className="menu-list pl-0 mb-0 list-unstyled">
        {menu.columns.map((column) => (
          <li key={column.id}>
            <h2 id={column.id}>{column.title}</h2>
            <ul className="menu-list pl-0 mb-0 list-unstyled">
              {column.links.map((link) => (
                <li key={link.id}><a href={menuHref(link)} id={link.id}>{link.label}</a></li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      {menu.promos.length > 0 && (
        <div is="m-menu-inspiration">
          <div className="grid grid-col-4">
            {menu.promos.map((promo, index) => (
              <div key={promo.id} id={`menu-section-item-${index}`} className="menu-section">
                <h2>{promo.title}{promo.subtitle ? <><br />{promo.subtitle}</> : null}</h2>
                <p>
                  <a href={promo.href || '#'}><img alt={promo.alt} src={promo.image} /></a>
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** The same menu inside the mobile drawer, where each column is its own sub-level. */
function ProductsDrawer({ attributes, menu }) {
  const props = attributesToProps(attributes);
  const back = (
    <li className="close-menu clearfix">
      <div className="back pull-left">
        <button type="button" aria-label="Back to level 1 of the menu">
          <span className="caret-left" />
          Back
        </button>
      </div>
    </li>
  );

  return (
    <div {...props}>
      <ul className="dropdown-menu" aria-label="MONALISA LIVING" aria-labelledby="products">
        {back}
        {menu.columns.map((column) => (
          <li key={column.id} className="dropdown-item dropdown">
            {/* Matches the static markup: the heading opens its sub-level, it never navigates. */}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a id={`drawer-${column.id}`} className="dropdown-link" role="presentation">{column.title}</a>
            <ul className="dropdown-menu" aria-label="MONALISA LIVING" aria-labelledby={`drawer-${column.id}`}>
              {back}
              {column.links.map((link) => (
                <li key={link.id} className="dropdown-item">
                  <a href={menuHref(link)} className="dropdown-link">{link.label}</a>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CategoryMenu({ attributes, open, onClose, children }) {
  const props = attributesToProps(attributes);
  const baseClassName = (props.className || '').split(' ').filter((name) => name && name !== 'd-none').join(' ');

  return (
    <div {...props} className={`${baseClassName} custom-category-menu${open ? ' in is-open' : ''}`.trim()}
      aria-hidden={!open}
      onClickCapture={(event) => {
        const link = event.target.closest && event.target.closest('a');
        const navigationTarget = event.target.closest && event.target.closest('a, button');
        const href = link?.getAttribute('href') || navigationTarget?.getAttribute('data-href') || '';
        const productMatch = href.match(/\/en\/products\/([^/?#]+)/);
        const cleanProductMatch = href.match(/^\/products\/([^/?#]+)/);
        const productSlug = productMatch?.[1] || cleanProductMatch?.[1];
        const contentSlug = CONTENT_PAGE_LINKS[(link?.textContent || '').trim().toLowerCase()];
        const isProductsLanding = (navigationTarget?.textContent || '').trim().toLowerCase() === 'products';
        const cleanPath = href.replace(/^\//, '').replace(/\/$/, '');
        if (isProductsLanding) {
          event.preventDefault();
          navigateTo('/products');
          onClose();
        } else if (productSlug && PRODUCT_CATEGORY_LABELS[productSlug]) {
          event.preventDefault();
          navigateTo(`/products/${productSlug}`);
          onClose();
        } else if (COLLECTION_PAGES[cleanPath]) {
          event.preventDefault();
          navigateTo(`/${cleanPath}`);
          onClose();
        } else if (contentSlug) {
          event.preventDefault();
          navigateTo(`/${contentSlug}`);
          onClose();
        } else if (link && !href.startsWith('#/')) {
          event.preventDefault();
          link.classList.add('is-inert-link');
        }
      }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      {children}
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [route, setRoute] = useState(getRoute);
  const [menu, setMenu] = useState(bootstrapMegaMenu || EMPTY_MENU);
  const [pages, setPages] = useState(bootstrapPages || []);
  const [searchProducts, setSearchProducts] = useState(bootstrapProducts || []);
  const [pagesLoading, setPagesLoading] = useState(!bootstrapPages);

  useEffect(() => {
    const syncRoute = () => {
      setRoute(getRoute());
      setMenuOpen(false);
      setSearchOpen(false);
    };

    window.addEventListener('hashchange', syncRoute);
    window.addEventListener('popstate', syncRoute);
    return () => {
      window.removeEventListener('hashchange', syncRoute);
      window.removeEventListener('popstate', syncRoute);
    };
  }, []);

  useEffect(() => {
    if (bootstrapPages) return undefined;
    let active = true;
    fetchPages().then((payload) => { if (active) setPages(payload); }).catch(() => {}).finally(() => {
      if (active) setPagesLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (bootstrapProducts) return undefined;
    let active = true;
    fetchProducts().then((payload) => { if (active) setSearchProducts(payload); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (bootstrapMegaMenu) return undefined;
    let active = true;
    fetchMegaMenu().then((payload) => {
      if (active) setMenu(adoptMenuCategories(payload) || EMPTY_MENU);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const closeAll = () => {
      document.querySelectorAll('.nav-item.dropdown.show, .dropdown-menu.show').forEach((node) => {
        node.classList.remove('show');
      });
      document.querySelectorAll('.nav-item.dropdown .dropdown-toggle[aria-expanded="true"]').forEach((node) => {
        node.setAttribute('aria-expanded', 'false');
      });
    };

    const onClick = (event) => {
      const closeButton = event.target.closest('.close-desktop-menu');
      if (closeButton) {
        event.preventDefault();
        closeAll();
        return;
      }

      const toggle = event.target.closest('.nav-item.dropdown > .dropdown-toggle');
      if (toggle) {
        event.preventDefault();
        const item = toggle.parentElement;
        const menu = item.querySelector(':scope > .dropdown-menu');
        const wasOpen = menu ? menu.classList.contains('show') : false;
        closeAll();
        if (menu && !wasOpen) {
          item.classList.add('show');
          menu.classList.add('show');
          toggle.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      if (!event.target.closest('.dropdown-menu')) closeAll();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeAll();
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
      closeAll();
    };
  }, []);

  useEffect(() => {
    const header = document.querySelector('header[role="banner"]');
    if (!header) return undefined;

    const syncHeaderHeight = () => {
      const height = `${Math.round(header.getBoundingClientRect().height)}px`;
      document.documentElement.style.setProperty('--header-height', height);
      document.documentElement.style.setProperty('--header-height-mobile', height);
    };

    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);
    window.addEventListener('orientationchange', syncHeaderHeight);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHeaderHeight) : null;
    if (observer) observer.observe(header);

    return () => {
      window.removeEventListener('resize', syncHeaderHeight);
      window.removeEventListener('orientationchange', syncHeaderHeight);
      if (observer) observer.disconnect();
    };
  }, []);

  return useMemo(() => {
    // The content studio is a server-rendered Laravel panel now; #/admin forwards to it.
    if (route === 'admin') {
      window.location.replace('/admin');
      return null;
    }

    const hasMenu = menu.columns.length > 0;
    let videoIndex = 0;
    let mediaSlot = 0;
    let mediaStackDone = false;

    const options = {
      replace(node) {
        if (node.type === 'tag' && node.name === 'li' && containsHiddenPrimaryMenuItem(node)) {
          return <></>;
        }

        if (node.type === 'tag' && node.name === 'button' && node.attribs?.id === 'products') {
          return <a href="/products" className={node.attribs.class || 'nav-link'}>
            {domToReact(node.children, options)}
          </a>;
        }

        if (node.type === 'tag' && node.name === 'button' && node.attribs?.name === 'burgerMenu') {
          return <MenuToggle open={menuOpen} onToggle={() => {
            setMenuOpen((current) => !current);
            setSearchOpen(false);
          }} />;
        }

        if (node.type === 'tag' && node.attribs?.id === 'sg-navbar-collapse') {
          return (
            <CategoryMenu attributes={node.attribs} open={menuOpen} onClose={() => setMenuOpen(false)}>
              {domToReact(node.children, options)}
            </CategoryMenu>
          );
        }

        // The header product menu is admin-managed; the static markup is the fallback
        // for the brief moment before the menu payload lands.
        if (hasMenu && node.type === 'tag' && node.name === 'div'
          && node.attribs?.class === 'd-flex' && node.parent?.attribs?.id === 'menu-products') {
          return <ProductsFlyout menu={menu} />;
        }

        if (hasMenu && node.type === 'tag' && node.name === 'div'
          && node.attribs?.class?.split(' ').includes('level2-products')) {
          return <ProductsDrawer attributes={node.attribs} menu={menu} />;
        }

        if (node.type === 'tag' && node.name === 'ul' && node.attribs?.class?.split(' ').includes('collection-links')) {
          return <CollectionLinks onNavigate={() => setMenuOpen(false)} />;
        }

        if (node.type === 'tag' && node.name === 'main' && node.attribs?.id === 'maincontent' && route !== 'home') {
          if (route.startsWith('checkout/')) return <CheckoutPage slug={route.slice(9)} products={searchProducts} />;
          if (route.startsWith('thank-you/')) return <ThankYouPage number={route.slice(10)} />;
          if (route.startsWith('product/')) return <ProductDetailPage slug={route.slice(8)} products={searchProducts} />;
          if (route.startsWith('pages/')) return <ContentPage slug={route.slice(6)} pages={pages} loading={pagesLoading} />;
          if (route.startsWith('products/')) return <ProductCategoryPage slug={route.slice(9)} />;
          if (route === 'the-deco-boutique') return <DecoBoutiquePage adminProducts={searchProducts} />;
          if (route === 'spring-summer') return <SpringSummerPage adminProducts={searchProducts} />;
          if (route === 'outdoor-collection') return <OutdoorCollectionPage adminProducts={searchProducts} />;
          if (ACCOUNT_ROUTES.includes(route)) {
            return <AccountPage mode={route === 'register' ? 'register' : 'login'}
              onNavigateHome={() => navigateTo('/')} />;
          }
          return <CollectionPage slug={route} />;
        }

        if (node.type === 'tag' && node.name === 'div' && node.attribs?.class?.split(' ').includes('search')) {
          return <SearchControl open={searchOpen} products={searchProducts} onToggle={() => {
            setSearchOpen((current) => !current);
            setMenuOpen(false);
          }} />;
        }

        if (node.type === 'tag' && node.name === 'footer' && node.attribs?.id === 'footercontent') {
          return <DigitalWebarsFooter />;
        }

        if (node.type === 'tag' && node.name === 'a' && ACCOUNT_LINK_PATTERN.test(node.attribs?.href || '')) {
          const props = attributesToProps(node.attribs);
          return <a {...props} href="/login">{domToReact(node.children, options)}</a>;
        }

        if (node.type === 'tag' && node.name === 'img' && node.attribs?.src?.endsWith('/images/logo.svg')) {
          const props = attributesToProps(node.attribs);
          return (
            <img {...props} src="/monalisa-living-logo.png" alt="MONALISA LIVING"
              className={`${props.className || ''} monalisa-brand-logo`.trim()} />
          );
        }

        // The whole home media stack (videos + banners) is admin-managed now:
        // the first static media component becomes the dynamic stack, the rest are dropped.
        if (route === 'home' && node.type === 'tag' && typeof node.attribs?.class === 'string') {
          const classes = node.attribs.class.split(' ');
          const isVideo = classes.includes('experience-assets-playerVideoWistiaSecondary');
          const isBanner = classes.includes('experience-assets-mainBanner');
          // Only the tiles inside the media stack are replaced; the tile grid further
          // down the page keeps its own markup.
          const isStackTile = classes.includes('experience-assets-contentTile') && !mediaStackDone;

          if (isVideo || isBanner || isStackTile) {
            mediaSlot += 1;
            if (isVideo) videoIndex += 1;
            if (videoIndex >= STATIC_VIDEO_COUNT) mediaStackDone = true;
            return mediaSlot === 1 ? <HomeMediaSections /> : <></>;
          }
        }

      },
    };

    return (
      <>{parse(pageMarkup, options)}</>
    );
  }, [menuOpen, searchOpen, route, menu, pages, pagesLoading, searchProducts]);
}

export default App;
